import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from '@amzn/awsui-components-core';
import styles from './styles/EmbedApp.module.scss';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { logger } from 'src/logger';

interface TableauVizComponentProps {
  src: string;
  title: string;
}

const TABLEAU_API_LOAD_TIMEOUT_MS = 15000;
const FINSUITE_NAV_HEIGHT_PX = 48;

export const toTableauEmbedUrl = (url: string): string => {
  return url.replace(/\/#\/site\//, '/t/');
};

const getTableauPodBaseUrl = (url: string): string => {
  const parsed = new URL(url);
  return parsed.origin;
};

let tableauLoadPromise: Promise<void> | null = null;

const loadTableauScript = (podBaseUrl: string): Promise<void> => {
  if (tableauLoadPromise) return tableauLoadPromise;

  tableauLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'tableau-embedding-api-v3';
    script.type = 'module';
    script.src = `${podBaseUrl}/javascripts/api/tableau.embedding.3.latest.min.js`;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      tableauLoadPromise = null;
      reject(new Error('Failed to load Tableau Embedding API'));
    };
    document.head.appendChild(script);
  });
  return tableauLoadPromise;
};

type LoadPhase = 'loading-api' | 'viz-loading' | 'viz-ready' | 'error';

const getAvailableHeight = (): number => window.innerHeight - FINSUITE_NAV_HEIGHT_PX;

const TableauVizComponent: React.FC<TableauVizComponentProps> = ({ src, title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const vizRef = useRef<HTMLElement | null>(null);
  const disposedRef = useRef(false);
  const [phase, setPhase] = useState<LoadPhase>('loading-api');
  const [error, setError] = useState<string | null>(null);

  const updateVizHeight = useCallback(() => {
    if (vizRef.current) {
      vizRef.current.setAttribute('height', `${getAvailableHeight()}px`);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateVizHeight);
    return () => window.removeEventListener('resize', updateVizHeight);
  }, [updateVizHeight]);

  useEffect(() => {
    disposedRef.current = false;

    const embedUrl = toTableauEmbedUrl(src);
    const podBaseUrl = getTableauPodBaseUrl(embedUrl);

    const apiLoadTimeout = setTimeout(() => {
      if (!disposedRef.current) {
        setPhase((prev) => {
          if (prev === 'loading-api') {
            setError('Tableau API failed to load within the expected time');
            return 'error';
          }
          return prev;
        });
      }
    }, TABLEAU_API_LOAD_TIMEOUT_MS);

    loadTableauScript(podBaseUrl)
      .then(() => {
        clearTimeout(apiLoadTimeout);
        if (disposedRef.current || !containerRef.current) return;

        const viz = document.createElement('tableau-viz');
        viz.setAttribute('src', embedUrl);
        viz.setAttribute('toolbar', 'top');
        viz.setAttribute('width', '100%');
        viz.setAttribute('height', `${getAvailableHeight()}px`);
        viz.setAttribute('hide-edit-button', '');
        viz.setAttribute('auth-type', 'popup');

        viz.addEventListener('firstinteractive', () => {
          if (!disposedRef.current) {
            setPhase('viz-ready');
            logger.info('Tableau viz rendered successfully', { url: embedUrl });
          }
        });

        viz.addEventListener('vizloaderror', ((event: CustomEvent) => {
          if (!disposedRef.current) {
            const detail = event?.detail?.message || 'Unknown Tableau error';
            setError(`Tableau visualization failed to load: ${detail}`);
            setPhase('error');
            logger.error('Tableau viz load error', { error: detail, url: embedUrl });
          }
        }) as EventListener);

        containerRef.current.appendChild(viz);
        vizRef.current = viz;
        setPhase('viz-loading');
      })
      .catch((err) => {
        clearTimeout(apiLoadTimeout);
        if (!disposedRef.current) {
          setError(err.message || 'Failed to load Tableau Embedding API');
          setPhase('error');
          logger.error('Tableau API script load failed', { error: err });
        }
      });

    return () => {
      disposedRef.current = true;
      clearTimeout(apiLoadTimeout);
      if (vizRef.current) {
        try {
          (vizRef.current as unknown as { dispose?: () => void }).dispose?.();
        } catch {
          // dispose may not be available
        }
        vizRef.current.remove();
        vizRef.current = null;
      }
    };
  }, [src]);

  return (
    <div className={styles.iframeContainer} aria-label={title}>
      {phase === 'loading-api' && (
        <div className={styles.centeredStateContainer}>
          <LoadingSpinner />
        </div>
      )}
      {phase === 'error' && (
        <div className={styles.centeredStateContainer}>
          <div className={styles.centeredStateContentWrapper}>
            <Alert type="error">{error}</Alert>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: phase === 'error' || phase === 'loading-api' ? 'none' : 'block'
        }}
      />
    </div>
  );
};

export default TableauVizComponent;
